import React, { useState } from 'react';
import { Check, ArrowRight, Edit2, Copy } from 'lucide-react';
import type { Formation } from '../../data/sportsFormations';
import { 
  FOOTBALL_OFFENSIVE_FORMATIONS, 
  FOOTBALL_DEFENSIVE_FORMATIONS,
  FLAG_FOOTBALL_OFFENSIVE_FORMATIONS,
  SOCCER_FORMATIONS
} from '../../data/sportsFormations';
import type { Sport } from '../../types';
import type { FormationPosition } from './VisualFormationBuilder';

// Re-export for convenience
export type { FormationPosition };

interface SampleFormationsProps {
  sport: Sport;
  unit: 'Offense' | 'Defense' | 'Special Teams';
  onSelectFormation: (positions: FormationPosition[], formationName: string) => void;
  onUseAsTemplate: (positions: FormationPosition[], formationName: string) => void;
}

const SampleFormations: React.FC<SampleFormationsProps> = ({
  sport,
  unit,
  onSelectFormation,
  onUseAsTemplate
}) => {
  const [selectedFormation, setSelectedFormation] = useState<string | null>(null);

  // Get formations for current sport and unit
  const getFormations = (): Formation[] => {
    if (sport === 'Football') {
      if (unit === 'Offense') {
        return FOOTBALL_OFFENSIVE_FORMATIONS;
      } else if (unit === 'Defense') {
        return FOOTBALL_DEFENSIVE_FORMATIONS;
      }
    } else if (sport === 'FlagFootball') {
      if (unit === 'Offense') {
        return FLAG_FOOTBALL_OFFENSIVE_FORMATIONS;
      }
    } else if (sport === 'Soccer') {
      return SOCCER_FORMATIONS;
    }
    return [];
  };

  const formations = getFormations();

  // Convert Formation to FormationPosition
  const convertToFormationPositions = (formation: Formation): FormationPosition[] => {
    const positions = formation.positions;
    
    // Separate OL positions from skill positions
    const olPositions = positions.filter(p => ['LT', 'LG', 'C', 'RG', 'RT'].includes(p.key));
    const skillPositions = positions.filter(p => !['LT', 'LG', 'C', 'RG', 'RT'].includes(p.key));
    
    const result: FormationPosition[] = [];
    
    // Add OL positions with proper positioning and mapping
    if (olPositions.length > 0 && sport === 'Football' && unit === 'Offense') {
      const olMap: Record<string, { x: number; label: string }> = {
        'LT': { x: 35, label: 'LT' },
        'LG': { x: 42.5, label: 'LG' },
        'C': { x: 50, label: 'C' },
        'RG': { x: 57.5, label: 'RG' },
        'RT': { x: 65, label: 'RT' },
      };
      
      olPositions.forEach((pos) => {
        const olPos = olMap[pos.key];
        if (olPos) {
      result.push({
        id: `ol_${pos.key.toLowerCase()}_${Date.now()}`,
        label: olPos.label.toUpperCase(), // Always uppercase
        fullName: pos.fullName,
        x: olPos.x,
        y: 50, // Middle of field
        positionGroupKey: 'OL', // Auto-map to OL
        color: pos.color || '#1E40AF', // Darker blue for OL
        isCenter: pos.key === 'C',
      });
        }
      });
    }
    
    // Add skill positions with grid layout
    const cols = Math.ceil(Math.sqrt(skillPositions.length));
    skillPositions.forEach((pos, index) => {
      const row = Math.floor(index / cols);
      const col = index % cols;
      const x = (col + 1) * (100 / (cols + 1));
      const y = (row + 1) * (100 / (Math.ceil(skillPositions.length / cols) + 1));
      
      // Try to infer position group from key
      let positionGroupKey: string | undefined;
      let defaultColor = '#1E40AF'; // Default darker blue
      
      // Offensive positions
      if (pos.key === 'QB') {
        positionGroupKey = 'QB';
        defaultColor = '#DC2626'; // Darker red for QB
      } else if (['RB', 'FB', 'HB'].includes(pos.key)) {
        positionGroupKey = 'RB';
      } else if (['X', 'Z', 'Y', 'SL', 'H', 'WR'].includes(pos.key)) {
        positionGroupKey = 'WR';
      } else if (['Y', 'TE', 'U'].includes(pos.key)) {
        positionGroupKey = 'TE';
      }
      // Defensive positions
      else if (['LDE', 'RDE', 'LDT', 'RDT', 'NT', 'DE', 'DT', 'EDGE'].includes(pos.key)) {
        positionGroupKey = 'DL';
      } else if (['WLB', 'MLB', 'SLB', 'LOLB', 'ROLB', 'LILB', 'RILB', 'ILB1', 'ILB2', 'OLB1', 'OLB2', 'LB'].includes(pos.key)) {
        positionGroupKey = 'LB';
      } else if (['LCB', 'RCB', 'FS', 'SS', 'NB', 'CB', 'S'].includes(pos.key)) {
        positionGroupKey = 'DB';
      }
      
      result.push({
        id: `pos_${index}_${Date.now()}`,
        label: pos.name.toUpperCase(), // Always uppercase
        fullName: pos.fullName,
        x: Math.max(10, Math.min(90, x)),
        y: Math.max(10, Math.min(80, y)), // Keep skill positions above OL
        positionGroupKey,
        color: pos.color || defaultColor,
        isCenter: false,
      });
    });
    
    return result;
  };

  const handleUseAsIs = (formation: Formation) => {
    const positions = convertToFormationPositions(formation);
    onSelectFormation(positions, formation.name);
  };

  const handleUseAsTemplate = (formation: Formation) => {
    const positions = convertToFormationPositions(formation);
    onUseAsTemplate(positions, formation.name);
  };

  if (formations.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No sample formations available for {sport} - {unit}</p>
        <p className="text-sm mt-2">You can create a custom formation instead</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Sample Formations</h3>
          <p className="text-sm text-gray-600 mt-1">
            Choose a formation to use as-is or modify to fit your needs
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {formations.map((formation) => (
          <div
            key={formation.name}
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedFormation === formation.name
                ? 'border-primary bg-primary bg-opacity-5'
                : 'border-gray-200 hover:border-primary hover:shadow-md'
            }`}
            onClick={() => setSelectedFormation(
              selectedFormation === formation.name ? null : formation.name
            )}
          >
            <div className="flex items-start justify-between mb-2">
              <h4 className="font-semibold text-lg">{formation.name}</h4>
              {selectedFormation === formation.name && (
                <Check className="w-5 h-5 text-primary" />
              )}
            </div>

            {formation.description && (
              <p className="text-sm text-gray-600 mb-3">{formation.description}</p>
            )}

            <div className="mb-3">
              <p className="text-xs text-gray-500 mb-1">
                {formation.positions.length} positions
              </p>
              <div className="flex flex-wrap gap-1">
                {formation.positions.slice(0, 6).map((pos) => (
                  <span
                    key={pos.key}
                    className="px-2 py-1 bg-gray-100 rounded text-xs"
                  >
                    {pos.name}
                  </span>
                ))}
                {formation.positions.length > 6 && (
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                    +{formation.positions.length - 6} more
                  </span>
                )}
              </div>
            </div>

            {selectedFormation === formation.name && (
              <div className="flex gap-2 mt-3 pt-3 border-t">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUseAsIs(formation);
                  }}
                  className="btn btn-primary btn-sm flex-1 flex items-center gap-2"
                >
                  <Check className="w-4 h-4" />
                  Use As-Is
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUseAsTemplate(formation);
                  }}
                  className="btn btn-outline btn-sm flex-1 flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Customize
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-blue-800">
          <strong>Tip:</strong> You can use any sample formation as-is, or customize it to match your team's needs. 
          You can also create a completely custom formation from scratch.
        </p>
      </div>
    </div>
  );
};

export default SampleFormations;

